import { forwardRef, useCallback, useLayoutEffect, useRef } from 'react';
import type { FunctionComponent, Ref } from 'react';

import Flex from '../../Flex';
import type { FormControlProps } from '../../FormControl';
import { useConsolidatedRef, useFocusWithin, useI18n, useTestIds, useUID } from '../../../hooks';
import type DateTimeProps from '../DateTime.types';
import type {
  DateRangeCallbackParameter,
  DateTimeCallbackParameter,
  DateTimeProperties
} from '../DateTime.types';
import type { ForwardProps } from '../../../types';
import { StyledRangeInputContainer } from '../../Input/Input.styles';
import type { MinuteInterval } from '../Picker/DatePicker.types';
import FormField from '../../FormField';
import { getTimeRangeInputTestIds } from '../DateTime.test-ids';
import { createClassName, withTestIds } from '../../../utils';
import HiddenText from '../../HiddenText';

import { parseToDate } from './utils';
import type { ClockFormat } from './utils';
import TimeInput from './TimeInput';
import DateTimeInput from './DateTimeInput';

export interface TimeRangeInputProps extends DateTimeProperties {
  /** Object of range boundaries. */
  value?: { start?: DateTimeProps['value']; end?: DateTimeProps['value'] };
  /** Callback fired when inputs value changes. */
  onChange: (value: DateRangeCallbackParameter) => void;
  /** Callback fired when the component gets focused. */
  onFocus?: (value: DateRangeCallbackParameter) => void;
  /** Callback fired when the component loses focus. */
  onBlur?: (value: DateRangeCallbackParameter) => void;
  /** Pass a string or a fragment with an Icon and string for first part of TimeRangeInput. */
  fromLabel?: FormControlProps['label'];
  /** Pass a string or a fragment with an Icon and string for second part of TimeRangeInput. */
  toLabel?: FormControlProps['label'];
  /** Pass an object with "heading" and "content" properties to show AdditionalInfo for second part of DataRangeInput. */
  toAdditionalInfo?: FormControlProps['additionalInfo'];
  /** Pass an object with "heading" and "content" properties to show AdditionalInfo for first part of DataRangeInput. */
  fromAdditionalInfo?: FormControlProps['additionalInfo'];
  /** Controls which input is displayed */
  mode: 'time' | 'datetime';
  /**
   * Display the time with seconds.
   * @default false
   */
  withSeconds?: boolean;
  /**
   * Defines interval for time picker options (in minutes).
   * @default 30
   */
  pickerInterval?: MinuteInterval;
  /** Defines clock format that overrides locale based format */
  clockFormat?: ClockFormat;
}

const convertToCallbackParameter = (v?: number): DateTimeCallbackParameter => {
  return {
    valueAsISOString: v === undefined || Number.isNaN(v) ? undefined : new Date(v).toISOString(),
    valueAsTimestamp: v
  };
};

const TimeRangeInput: FunctionComponent<TimeRangeInputProps & ForwardProps> = forwardRef(
  function TimeRangeInput(
    {
      testId,
      value = {},
      onChange,
      onFocus,
      onBlur,
      label,
      fromLabel,
      toLabel,
      labelHidden,
      required,
      readOnly,
      disabled,
      min,
      max,
      withSeconds,
      pickerInterval,
      clockFormat,
      mode,
      status,
      timeZone,
      fromAdditionalInfo,
      toAdditionalInfo,
      autoFocus,
      className,
      ...restProps
    }: TimeRangeInputProps,
    ref: Ref<HTMLDivElement>
  ) {
    const testIds = useTestIds(testId, getTimeRangeInputTestIds);

    const t = useI18n();

    const start =
      value.start === undefined || value.start === ''
        ? undefined
        : parseToDate(value.start).getTime();
    const end =
      value.end === undefined || value.end === '' ? undefined : parseToDate(value.end).getTime();

    const startTimeRef = useRef<DateTimeCallbackParameter>(convertToCallbackParameter(start));
    const endTimeRef = useRef<DateTimeCallbackParameter>(convertToCallbackParameter(end));

    useLayoutEffect(() => {
      startTimeRef.current = convertToCallbackParameter(start);
      endTimeRef.current = convertToCallbackParameter(end);
    }, [start, end]);

    const onFocusChange = useCallback(
      (focused: boolean) => {
        (focused ? onFocus : onBlur)?.({
          start: startTimeRef.current,
          end: endTimeRef.current
        });
      },
      [onFocus, onBlur]
    );

    const containerRef = useConsolidatedRef(ref);
    useFocusWithin([containerRef], onFocusChange);

    const commonProps = {
      required,
      readOnly,
      disabled,
      min,
      max,
      status,
      withSeconds,
      pickerInterval,
      clockFormat,
      timeZone
    };

    const descriptionId = useUID();

    return (
      <>
        <FormField
          testId={testIds}
          as='fieldset'
          {...restProps}
          container={{ wrap: 'nowrap' }}
          ref={containerRef}
          label={label}
          labelHidden={labelHidden}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          status={status}
          aria-describedby={descriptionId}
        >
          <Flex
            container
            as={StyledRangeInputContainer}
            className={createClassName('time-range-input', className)}
          >
            {mode === 'time' ? (
              <>
                <TimeInput
                  testId={testIds.fromTime}
                  {...commonProps}
                  value={value.start}
                  onBlur={changedValue => {
                    startTimeRef.current = changedValue;
                    if (!Number.isNaN(end)) endTimeRef.current = convertToCallbackParameter(end);
                    onChange({
                      start: changedValue,
                      end: convertToCallbackParameter(end)
                    });
                  }}
                  label={fromLabel}
                  aria-label={`${t('range_from_datetime') + (label ? ` - ${label}` : '')}`}
                  additionalInfo={fromAdditionalInfo}
                  autoFocus={autoFocus}
                />
                <TimeInput
                  testId={testIds.toTime}
                  {...commonProps}
                  value={value.end}
                  onBlur={changedValue => {
                    if (!Number.isNaN(start))
                      startTimeRef.current = convertToCallbackParameter(start);
                    endTimeRef.current = changedValue;
                    onChange({
                      start: convertToCallbackParameter(start),
                      end: changedValue
                    });
                  }}
                  label={toLabel}
                  aria-label={`${t('range_to_datetime') + (label ? ` - ${label}` : '')}`}
                  additionalInfo={toAdditionalInfo}
                />
              </>
            ) : (
              <>
                <DateTimeInput
                  testId={testIds.fromDateTime}
                  {...commonProps}
                  value={value.start}
                  onBlur={changedValue => {
                    startTimeRef.current = changedValue;
                    if (!Number.isNaN(end)) endTimeRef.current = convertToCallbackParameter(end);
                    onChange({
                      start: changedValue,
                      end: convertToCallbackParameter(end)
                    });
                  }}
                  label={fromLabel}
                  aria-label={`${t('range_from_datetime') + (label ? ` - ${label}` : '')}`}
                  additionalInfo={fromAdditionalInfo}
                />
                <DateTimeInput
                  testId={testIds.toDateTime}
                  {...commonProps}
                  value={value.end}
                  onBlur={changedValue => {
                    if (!Number.isNaN(start))
                      startTimeRef.current = convertToCallbackParameter(start);
                    endTimeRef.current = changedValue;
                    onChange({
                      start: convertToCallbackParameter(start),
                      end: changedValue
                    });
                  }}
                  label={toLabel}
                  aria-label={`${t('range_to_datetime') + (label ? ` - ${label}` : '')}`}
                  additionalInfo={toAdditionalInfo}
                />
              </>
            )}
          </Flex>
        </FormField>
        <HiddenText id={descriptionId}>{t('range_input')}</HiddenText>
      </>
    );
  }
);
export default withTestIds(TimeRangeInput, getTimeRangeInputTestIds);
